import React, {
  useMemo,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DataGrid } from '@mui/x-data-grid';
import { CircularProgress, Tooltip } from '@mui/material';
import format from 'string-template';

import { InfoTag, TransparentTag } from '../../../components/Tags';
import ExternalCoachContext from '../../context/ExternalCoachContext';
import { SubTitle } from '../../../components/v2/Header';
import {
  ClientStatus,
  ActiveSubscriptionClientStatuses,
  ClientExcludingFilters,
  clientStatusLabel,
  filterChecks,
  NEW_CLIENTS_FOR_N_DAYS,
  filterExcludingChecks,
} from '../../utils/statusFilter';
import { getUserStatus } from '../../utils/userStatus';
import { sortDates } from '../../utils/dataGridSortingFunctions';
import { getPhoneNumberDigits } from '../../../utils/phoneNumber';
import { ALL_CLIENTS } from '../../pages/ManageClients/utils';
import useQuickSearch from '../../../hooks/useQuickSearch';
import { UserCategory } from '../../../Model/User';
import { categoriesForCoach, categoryMap } from '../../../utils/userCategory';
import LabelCheckbox from '../LabelCheckbox';
import Filter from '../Filter';
import QuickSearchToolbar from '../QuickSearchToolbar';
import UserProfileCard from '../UserProfileCard';
import ManageClientTags from '../ManageClientTags';
import PageSelectionHeader from './components/PageSelectionHeader';

import {
  DataGridContainer,
  CellContainer,
  ProductCellContainer,
  ProductName,
  StyledCheckBox,
  ChipContainer,
  InfoRow,
  StyledHelpIcon,
} from './styles';
import texts from './texts.json';

const STATUS_TOOLTIP_SEPARATOR = '#';

const UserTable = ({
  rows,
  clientSortModel,
  setClientSortModel,
  clientStatusFilters,
  setClientStatusFilters,
  clientExcludingFilters,
  setClientExcludingFilters,
  setSelectedClients,
  onClientsSelectionChange,
  selectedClients,
  clientTagsDocs,
  clientTagsFilters,
  setClientTagsFilters,
  loadingUsers,
  clientTablePaginationModel,
  setClientPaginationModel,
  selectedUserType,
}) => {
  const { products, isProductsReady, coachDoc } = useContext(ExternalCoachContext);
  const productCategories = useMemo(() => {
    const byProduct = categoryMap(products);
    return new Map(rows.map((user) => {
      const held = categoriesForCoach(byProduct, user.userDoc, coachDoc?.id);
      return [user.id, held.size ? held : new Set([UserCategory.OTHER])];
    }));
  }, [products, rows, coachDoc]);
  const [onlyNewClients, setOnlyNewClients] = useState(false);

  useEffect(() => {
    const filters = clientExcludingFilters.filter((csf) => csf !== ClientExcludingFilters.NEW_CLIENT);
    if (onlyNewClients) {
      filters.push(ClientExcludingFilters.NEW_CLIENT);
    }
    // As we're setting here one and only one value, comparison of length is enough to decide if changes were made.
    if (filters.length !== clientExcludingFilters.length) {
      setClientExcludingFilters(filters);
    }
  }, [
    onlyNewClients,
    clientExcludingFilters,
    setClientExcludingFilters,
  ]);

  const productNames = useMemo(() => products.reduce((codes, {
    id: productId,
    name,
  }) => ({
    ...codes,
    [productId]: name,
  }), {}), [products]);

  const renderTags = useCallback(({ row: { clientTags } = {} }) => {
    if (clientTags && clientTags.length > 0) {
      const firstTag = clientTags.slice(0, 1);
      const remainingTags = clientTags.slice(1, clientTags.length);
      const tooltipText = remainingTags.map((rst) => rst).join(', ');
      return (
        <CellContainer>
          <ChipContainer>
            {firstTag.map((restrictionTag) => (
              <InfoTag key={restrictionTag}>{restrictionTag}</InfoTag>
            ))}
            {!!remainingTags.length && (
              <Tooltip title={tooltipText} placement="top" arrow>
                <InfoTag>{format(texts.moreTags, { amount: remainingTags.length })}</InfoTag>
              </Tooltip>
            )}
          </ChipContainer>
        </CellContainer>
      );
    }
    return (
      <CellContainer>
        {texts.emptyCell}
      </CellContainer>
    );
  }, []);

  const renderProducts = useCallback(({ row: { productIds = [], productId } = {} }) => {
    const otherProductIds = productIds.filter((id) => id !== productId);
    const tooltipText = otherProductIds.map((id) => productNames[id] || id).join(', ');

    return (
      <ProductCellContainer>
        <ProductName title={productNames[productId] || ''}>
          {productNames[productId] || texts.emptyCell}
          {!!otherProductIds.length && ','}
        </ProductName>
        {!!otherProductIds.length && (
          <Tooltip title={tooltipText} placement="top" arrow>
            <TransparentTag>{format(texts.moreProducts, { amount: otherProductIds.length })}</TransparentTag>
          </Tooltip>
        )}
      </ProductCellContainer>
    );
  }, [productNames]);

  const columns = useMemo(() => [
    {
      field: 'selectUsers',
      headerName: '',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <PageSelectionHeader
          selectedIds={selectedClients}
          onSelectionChange={onClientsSelectionChange}
          ariaLabel={texts.selectAllOnPage}
        />
      ),
      renderCell: (row) => (
        <StyledCheckBox
          size="small"
          checked={selectedClients.includes(row.id)}
          onChange={() => setSelectedClients(row.id)}
        />
      ),
    },
    {
      field: 'name',
      quickSearch: true,
      headerName: texts.headers.name,
      minWidth: 275,
      flex: 10,
      renderCell: ({ row }) => (
        <CellContainer>
          <UserProfileCard
            key={row.id}
            clientId={row.id}
            avatarSize="small"
            dataRow={{
              ...row,
              userName: row.name,
            }}
            showButtonsOnHover
          />
        </CellContainer>
      ),
    },
    {
      field: 'status',
      quickSearch: true,
      headerName: texts.headers.status,
      flex: 10,
      sortingOrder: ['asc', 'desc'],
      valueGetter: ({ row }) => {
        const {
          status,
          associatedDateString,
        } = getUserStatus(row);

        return `${status}${STATUS_TOOLTIP_SEPARATOR}${associatedDateString || ''}`;
      },
      renderCell: ({ value }) => {
        const [status, tooltipText] = value.split(STATUS_TOOLTIP_SEPARATOR);
        if (tooltipText) {
          return (
            <Tooltip
              title={tooltipText}
              placement="top"
              arrow
            >
              <CellContainer>{status}</CellContainer>
            </Tooltip>
          );
        }

        return <CellContainer>{status}</CellContainer>;
      },
    },
    {
      field: 'serviceStartAt',
      headerName: texts.headers.startDate,
      sortingOrder: ['asc', 'desc'],
      sortComparator: sortDates('serviceStartAt'),
      valueGetter: ({ row: { serviceStartAt } }) => {
        if (serviceStartAt) {
          return moment(serviceStartAt).fromNow();
        }
        return texts.emptyCell;
      },
      flex: 10,
      renderCell: ({ row: { serviceStartAt } }) => (
        <CellContainer title={serviceStartAt}>
          {`${serviceStartAt ? moment(serviceStartAt).fromNow() : texts.emptyCell}`}
        </CellContainer>
      ),
    },
    {
      field: 'lastCheckIn',
      headerName: texts.headers.lastCheckIn,
      flex: 10,
      sortingOrder: ['asc', 'desc'],
      sortComparator: sortDates('lastCheckIn'),
      valueGetter: ({ row: { lastCheckIn } }) => {
        if (lastCheckIn) {
          return moment(lastCheckIn).fromNow();
        }
        return texts.emptyCell;
      },
      renderCell: ({ row: { lastCheckIn } }) => (
        <CellContainer title={lastCheckIn}>
          {`${lastCheckIn && !moment(lastCheckIn).isSame(moment(new Date(0)))
            ? moment(lastCheckIn).fromNow()
            : texts.emptyCell}`}
        </CellContainer>
      ),
    },
    {
      field: 'lastWorkout',
      headerName: texts.headers.lastWorkout,
      flex: 10,
      sortingOrder: ['asc', 'desc'],
      sortComparator: sortDates('lastWorkout'),
      valueGetter: ({ row: { lastWorkout } }) => {
        if (lastWorkout) {
          return moment(lastWorkout).fromNow();
        }
        return texts.emptyCell;
      },
      renderCell: ({ row: { lastWorkout } }) => (
        <CellContainer title={lastWorkout}>
          {`${lastWorkout ? moment(lastWorkout).fromNow() : texts.emptyCell}`}
        </CellContainer>
      ),
    },
    {
      field: 'lastMessageAt',
      headerName: texts.headers.lastChatMessageAt,
      flex: 10,
      sortingOrder: ['asc', 'desc'],
      sortComparator: sortDates('lastMessageAt'),
      valueGetter: ({ row: { lastMessageAt } }) => {
        if (lastMessageAt) {
          return moment(lastMessageAt).fromNow();
        }
        return texts.emptyCell;
      },
      renderCell: ({ row: { lastMessageAt } }) => (
        <CellContainer title={lastMessageAt}>
          {`${lastMessageAt ? moment(lastMessageAt).fromNow() : texts.emptyCell}`}
        </CellContainer>
      ),
    },
    {
      field: 'productName',
      headerName: texts.headers.product,
      sortingOrder: ['asc', 'desc'],
      flex: 10,
      minWidth: 150,
      valueGetter: ({ row: { productId } }) => productNames[productId] || texts.emptyCell,
      renderCell: ({ row }) => (
        isProductsReady ? renderProducts({ row }) : <CellContainer><CircularProgress size={20} /></CellContainer>
      ),
    },
    {
      field: 'clientTags',
      headerName: texts.headers.tags,
      flex: 10,
      minWidth: 75,
      sortable: false,
      renderCell: ({ row }) => (
        renderTags({ row })
      ),
    },
    {
      field: 'commitmentEndingDate',
      headerName: texts.headers.commitmentEnding,
      flex: 10,
      sortingOrder: ['asc', 'desc'],
      sortComparator: sortDates('commitmentEndingDate'),
      valueGetter: ({ row: { commitmentEndingDate } }) => {
        if (commitmentEndingDate) {
          return moment(commitmentEndingDate).fromNow();
        }
        return texts.emptyCell;
      },
      renderCell: ({ row }) => (
        <CellContainer title={row.commitmentEndingDate}>
          {`${row.commitmentEndingDate ? moment(row.commitmentEndingDate).fromNow() : texts.emptyCell}`}
        </CellContainer>
      ),
    },
  ], [
    selectedClients,
    setSelectedClients,
    onClientsSelectionChange,
    renderTags,
    renderProducts,
    isProductsReady,
    productNames,
  ]);

  /*
   * Coaches search their clients by contact details as often as by name, but the table has no
   * room for those columns, so the emails and the phone number are declared as search-only
   * fields. Both emails are searched because they differ on secondary multi-coach accounts.
   * The phone number is normalized on both sides of the match so that the way either the
   * stored number or the typed one is formatted does not matter.
   */
  const searchColumns = useMemo(() => [
    ...columns,
    {
      field: 'email',
      quickSearch: true,
    },
    {
      field: 'accountEmail',
      quickSearch: true,
    },
    {
      field: 'phoneNumber',
      quickSearch: true,
      searchNormalizer: getPhoneNumberDigits,
    },
  ], [columns]);

  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(rows, searchColumns);

  const filteredRows = useMemo(() => quickSearchRows
    .filter((user) => clientStatusFilters.some((filter) => filterChecks[filter](user)))
    .filter((user) => !clientExcludingFilters.length
      || clientExcludingFilters.some((filter) => filterExcludingChecks[filter](user)))
    .filter((user) => clientTagsFilters.length === 0
      || user.clientTags.some((tag) => clientTagsFilters.includes(tag)))
    // A client holding two products belongs to both tabs.
    .filter((user) => selectedUserType === ALL_CLIENTS
      // Hide inactive clients (unpaid/canceled) from the AC clients list, show all other clients based on user category
      || (productCategories.get(user.id)?.has(selectedUserType)
        && ((selectedUserType === UserCategory.OTHER && user.isActive) || selectedUserType !== UserCategory.OTHER))
      // Add coach itself to free clients list
      || (selectedUserType === UserCategory.FREE_CLIENTS && user.id === coachDoc.id)),
  [
    quickSearchRows,
    clientStatusFilters,
    clientExcludingFilters,
    clientTagsFilters,
    selectedUserType,
    productCategories,
    coachDoc,
  ]);

  /*
   * We only show clients with an active subscription in the AC list
   * so we limit the options to the active subscription statuses.
   */
  const clientStatusOptions = useMemo(() => {
    const options = selectedUserType === UserCategory.OTHER
      ? ActiveSubscriptionClientStatuses
      : Object.values(ClientStatus);

    return options.map((status) => ({
      value: status,
      label: clientStatusLabel[status],
    }));
  }, [selectedUserType]);

  const tagsOptions = useMemo(() => (
    clientTagsDocs.map((doc) => ({
      value: doc.tag,
      label: doc.tag,
    }))
  ), [clientTagsDocs]);

  return (
    <DataGridContainer>
      {selectedUserType === UserCategory.OTHER && (
        <InfoRow>
          <StyledHelpIcon />
          <SubTitle>{texts.acClientsDescription}</SubTitle>
        </InfoRow>
      )}
      <DataGrid
        loading={loadingUsers}
        rows={filteredRows}
        columns={columns}
        rowHeight={50}
        autoPageSize
        pagination
        disableRowSelectionOnClick
        sortingOrder={['asc', 'desc']}
        sortModel={clientSortModel}
        onSortModelChange={(newSortModel) => setClientSortModel(newSortModel)}
        paginationModel={clientTablePaginationModel}
        onPaginationModelChange={setClientPaginationModel}
        components={{
          Toolbar: QuickSearchToolbar,
        }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.searchPlaceholder,
            processTools: [{
              Component: ManageClientTags,
              id: 'manage-client-tags',
              props: {
                selectedClientList: selectedClients,
                buttonLabel: selectedClients.length > 0
                  ? format(texts.tagClientsWithCount, { count: selectedClients.length })
                  : texts.tagClients,
              },
            }],
            filterTools: [
              {
                Component: Filter,
                id: 'tag-filter-toolbar',
                props: {
                  inputLabel: texts.clientTags,
                  options: tagsOptions,
                  initialValues: clientTagsFilters,
                  onValuesSelected: setClientTagsFilters,
                },
              },
              {
                Component: LabelCheckbox,
                id: 'new-client-filter-toolbar',
                props: {
                  description: format(texts.filterNewClients, { days: NEW_CLIENTS_FOR_N_DAYS }),
                  isChecked: onlyNewClients,
                  onChange: setOnlyNewClients,
                },
              },
              {
                Component: Filter,
                id: 'filter-toolbar',
                props: {
                  options: clientStatusOptions,
                  initialValues: clientStatusFilters,
                  title: texts.clientStatus,
                  onValuesSelected: setClientStatusFilters,
                },
              },
            ],
          },
        }}
      />
    </DataGridContainer>
  );
};

UserTable.propTypes = {
  rows: PropTypes.array.isRequired,
  clientSortModel: PropTypes.array.isRequired,
  setClientSortModel: PropTypes.func.isRequired,
  clientStatusFilters: PropTypes.array.isRequired,
  setClientStatusFilters: PropTypes.func.isRequired,
  clientExcludingFilters: PropTypes.array.isRequired,
  setClientExcludingFilters: PropTypes.func.isRequired,
  selectedClients: PropTypes.array.isRequired,
  // Toggles the selection of a single client id: (clientId) => void.
  setSelectedClients: PropTypes.func.isRequired,
  // Selects or deselects a group of client ids at once: (clientIds, isSelected) => void.
  onClientsSelectionChange: PropTypes.func.isRequired,
  clientTagsDocs: PropTypes.array.isRequired,
  clientTagsFilters: PropTypes.array.isRequired,
  setClientTagsFilters: PropTypes.func.isRequired,
  loadingUsers: PropTypes.bool.isRequired,
  clientTablePaginationModel: PropTypes.object,
  setClientPaginationModel: PropTypes.func.isRequired,
  selectedUserType: PropTypes.string.isRequired,
};

UserTable.defaultProps = {
  // If set the default value as null or {}, gives an error. That's why set as undefined.
  clientTablePaginationModel: undefined,
};

export default UserTable;
